import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import { toast } from 'react-toastify';
import { userService } from '../services';
import { User } from '../types';
import { formatUkDateLong } from '../utils/dateFormat';

type AddUserForm = {
  name: string;
  email: string;
  password: string;
  role: 'user' | 'admin';
  emailVerified: boolean;
  isActive: boolean;
  subscriptionPlan: 'none' | 'monthly' | 'yearly';
};

const defaultAddUserForm: AddUserForm = {
  name: '',
  email: '',
  password: '',
  role: 'user',
  emailVerified: true,
  isActive: true,
  subscriptionPlan: 'none',
};

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoadingUserId, setActionLoadingUserId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [addUserForm, setAddUserForm] = useState<AddUserForm>(defaultAddUserForm);
  const [addUserError, setAddUserError] = useState('');
  const [reactivateDialog, setReactivateDialog] = useState<{
    open: boolean;
    user: User | null;
    reason: string;
  }>({
    open: false,
    user: null,
    reason: 'User requested account reactivation',
  });
  const limit = 10;

  useEffect(() => {
    loadUsers();
  }, [page]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await userService.getAll(page, limit);
      const items = data?.items ?? [];
      setUsers(Array.isArray(items) ? items : []);
      setTotal(data?.total ?? 0);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load users');
      setUsers([]);
      setTotal(0);
      console.warn(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const openAddUserDialog = () => {
    setAddUserForm(defaultAddUserForm);
    setAddUserError('');
    setAddUserOpen(true);
  };

  const closeAddUserDialog = () => {
    if (addUserSaving) return;
    setAddUserOpen(false);
    setAddUserError('');
  };

  const handleCreateUser = async () => {
    if (!addUserForm.name.trim() || !addUserForm.email.trim() || !addUserForm.password) {
      setAddUserError('Name, email, and password are required.');
      return;
    }

    try {
      setAddUserSaving(true);
      setAddUserError('');
      await userService.create({
        name: addUserForm.name.trim(),
        email: addUserForm.email.trim(),
        password: addUserForm.password,
        role: addUserForm.role,
        emailVerified: addUserForm.emailVerified,
        isActive: addUserForm.isActive,
        subscriptionPlan: addUserForm.subscriptionPlan,
      });
      toast.success('User created successfully');
      setAddUserOpen(false);
      setAddUserForm(defaultAddUserForm);
      if (page !== 1) {
        setPage(1);
      } else {
        await loadUsers();
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        (Array.isArray(err?.response?.data?.message)
          ? err.response.data.message.join(', ')
          : null) ||
        'Failed to create user';
      setAddUserError(typeof message === 'string' ? message : 'Failed to create user');
    } finally {
      setAddUserSaving(false);
    }
  };

  const openReactivateDialog = (user: User) => {
    setReactivateDialog({
      open: true,
      user,
      reason: 'User requested account reactivation',
    });
  };

  const closeReactivateDialog = () => {
    setReactivateDialog({
      open: false,
      user: null,
      reason: 'User requested account reactivation',
    });
  };

  const handleReactivate = async () => {
    if (!reactivateDialog.user) return;
    try {
      setActionLoadingUserId(reactivateDialog.user._id);
      setError('');
      await userService.reactivate(reactivateDialog.user._id, reactivateDialog.reason);
      await loadUsers();
      closeReactivateDialog();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to reactivate user');
    } finally {
      setActionLoadingUserId(null);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      setActionLoadingUserId(user._id);
      setError('');
      await userService.updateStatus(user._id, !user.isActive);
      await loadUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to update user status');
    } finally {
      setActionLoadingUserId(null);
    }
  };

  const getUserStatus = (user: User): { label: string; color: 'success' | 'error' | 'default' } => {
    if (user.deletedAt) {
      return { label: 'Deleted', color: 'error' };
    }
    if (user.isActive) {
      return { label: 'Active', color: 'success' };
    }
    return { label: 'Inactive', color: 'default' };
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Typography variant="h4">Users Management</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {total > 0 && <Chip label={`Total: ${total}`} color="primary" />}
          <Button variant="contained" onClick={openAddUserDialog}>
            Add User
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Avatar</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
                <TableCell>Created At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No users found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Avatar src={user.avatar} alt={user.name}>
                        {user.name.charAt(0).toUpperCase()}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body1">{user.name}</Typography>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={user.role === 'admin' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const status = getUserStatus(user);
                        return (
                      <Chip
                        label={status.label}
                        color={status.color}
                        size="small"
                      />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {user.deletedAt ? (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openReactivateDialog(user)}
                          disabled={actionLoadingUserId === user._id}
                        >
                          {actionLoadingUserId === user._id ? 'Reactivating...' : 'Reactivate'}
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color={user.isActive ? 'warning' : 'success'}
                          onClick={() => handleToggleActive(user)}
                          disabled={actionLoadingUserId === user._id}
                        >
                          {actionLoadingUserId === user._id
                            ? 'Updating...'
                            : user.isActive
                              ? 'Deactivate'
                              : 'Activate'}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatUkDateLong(user.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {total > limit && (
          <Box
            className="pagination-bar"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              '& .MuiPagination-ul': { flexWrap: 'wrap', justifyContent: 'center' },
              '& .MuiPaginationItem-root': { fontWeight: 600 },
            }}
          >
            <Pagination
              count={Math.ceil(total / limit)}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
              size="medium"
            />
          </Box>
        )}
      </Card>

      <Dialog open={addUserOpen} onClose={closeAddUserDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {addUserError && <Alert severity="error">{addUserError}</Alert>}
            <TextField
              fullWidth
              required
              label="Name"
              value={addUserForm.name}
              onChange={(e) => setAddUserForm((prev) => ({ ...prev, name: e.target.value }))}
            />
            <TextField
              fullWidth
              required
              type="email"
              label="Email"
              value={addUserForm.email}
              onChange={(e) => setAddUserForm((prev) => ({ ...prev, email: e.target.value }))}
            />
            <TextField
              fullWidth
              required
              type="password"
              label="Password"
              helperText="8+ chars with upper, lower, number, and special character"
              value={addUserForm.password}
              onChange={(e) => setAddUserForm((prev) => ({ ...prev, password: e.target.value }))}
            />
            <FormControl fullWidth>
              <InputLabel id="add-user-role-label">Role</InputLabel>
              <Select
                labelId="add-user-role-label"
                label="Role"
                value={addUserForm.role}
                onChange={(e) =>
                  setAddUserForm((prev) => ({
                    ...prev,
                    role: e.target.value as 'user' | 'admin',
                  }))
                }
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="add-user-subscription-label">Subscription</InputLabel>
              <Select
                labelId="add-user-subscription-label"
                label="Subscription"
                value={addUserForm.subscriptionPlan}
                onChange={(e) =>
                  setAddUserForm((prev) => ({
                    ...prev,
                    subscriptionPlan: e.target.value as 'none' | 'monthly' | 'yearly',
                  }))
                }
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="monthly">Active monthly</MenuItem>
                <MenuItem value="yearly">Active yearly</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={addUserForm.emailVerified}
                  onChange={(e) =>
                    setAddUserForm((prev) => ({ ...prev, emailVerified: e.target.checked }))
                  }
                />
              }
              label="Email verified"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addUserForm.isActive}
                  onChange={(e) =>
                    setAddUserForm((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                />
              }
              label="Account active"
            />
            {addUserForm.role === 'admin' && (
              <Alert severity="info">
                Admin accounts are created successfully but are hidden from this users list.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddUserDialog} color="inherit" disabled={addUserSaving}>
            Cancel
          </Button>
          <Button onClick={handleCreateUser} variant="contained" disabled={addUserSaving}>
            {addUserSaving ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reactivateDialog.open} onClose={closeReactivateDialog} fullWidth maxWidth="sm">
        <DialogTitle>Reactivate User</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Optional reason for reactivating {reactivateDialog.user?.email}:
          </Typography>
          <TextField
            fullWidth
            autoFocus
            label="Reason"
            value={reactivateDialog.reason}
            onChange={(e) =>
              setReactivateDialog((prev) => ({
                ...prev,
                reason: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReactivateDialog} color="inherit" disabled={Boolean(actionLoadingUserId)}>
            Cancel
          </Button>
          <Button onClick={handleReactivate} variant="contained" disabled={Boolean(actionLoadingUserId)}>
            {actionLoadingUserId ? 'Reactivating...' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
